
import React from "react";
import { Button } from "@/components/ui/button";
import { Sun, ExternalLink, Shield, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useScreenSize } from "@/hooks/use-mobile";

interface AdminHeaderProps {
  onLogout: () => void;
}

const AdminHeader = ({ onLogout }: AdminHeaderProps) => {
  const { profile } = useAuth();
  const { isMobile } = useScreenSize();

  return (
    <header className="glass-card border-b border-border/50 py-3 px-4 sticky top-0 z-40 shadow-soft">
      <div className="flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="relative flex-shrink-0">
            <Sun className="h-5 w-5 sm:h-6 sm:w-6 text-peach-orange animate-glow" />
            <div className="absolute inset-0 h-5 w-5 sm:h-6 sm:w-6 text-peach-orange/30 animate-sunshine" />
          </div>
          <Link 
            to="/admin/dashboard" 
            className={`
              font-bold text-gradient-sunset hover-lift transition-all duration-300 truncate
              ${isMobile ? 'text-base' : 'text-xl'}
            `}
          >
            {isMobile ? 'Admin' : 'Follow The Sun Admin'}
          </Link>
          
          {!isMobile && (
            <div className="flex items-center gap-2 ml-4 bg-gradient-warm px-3 py-1 rounded-full">
              <Shield className="h-4 w-4 text-sunset-medium flex-shrink-0" />
              <span className="text-sm text-sunset-medium truncate font-medium">
                {profile?.name || profile?.email}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Public Site Link */}
          <Button asChild variant="ghost" size="sm" className={`
            hover:bg-gradient-warm hover:shadow-soft transition-all duration-300
            ${isMobile ? 'h-9 w-9 p-0' : 'h-9'}
          `}>
            <Link to="/" className={`flex items-center ${isMobile ? '' : 'gap-2'}`}>
              <ExternalLink className="h-4 w-4" />
              {!isMobile && <span>View Public Site</span>}
            </Link>
          </Button>
          
          {/* Mobile Profile Info */}
          {isMobile && (
            <div className="flex items-center gap-1 text-xs text-sunset-medium max-w-20 truncate bg-gradient-warm px-2 py-1 rounded-full">
              <Shield className="h-3 w-3 flex-shrink-0" />
              <span className="truncate font-medium">{profile?.name || 'Admin'}</span>
            </div>
          )}
          
          {/* Logout Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onLogout}
            className={`
              border-border bg-card hover:bg-gradient-warm hover:shadow-soft transition-all duration-300
              ${isMobile ? 'h-9 w-9 p-0' : 'h-9'}
            `}
          >
            <LogOut className="h-4 w-4" />
            {!isMobile && <span className="ml-1">Logout</span>}
          </Button>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
